import {createContext, useContext, useMemo, PropsWithChildren, useState, useCallback, useEffect} from 'react';

import {PageFilter} from 'filters/types';

type FilterStackState = {
  source: PageFilter | undefined;
  filter: PageFilter | undefined;
  loading: boolean;
  resolve: () => void;
};

const FilterStackContext = createContext<FilterStackState>(null as never);

FilterStackContext.displayName = 'FilterStack';

export const FilterSourceProvider = ({
  children,
  useFilter,
}: PropsWithChildren<{
  useFilter: (skip: boolean) => {
    filter: PageFilter | undefined;
    loading: boolean;
  };
}>) => {
  const [skip, setSkip] = useState(true);
  const {filter, loading} = useFilter(skip);

  return (
    <FilterStackContext.Provider
      value={{
        source: filter,
        filter,
        loading: loading || skip,
        resolve: useCallback(() => {
          setSkip(false);
        }, []),
      }}
    >
      {children}
    </FilterStackContext.Provider>
  );
};

export const FilterOverrideProvider = ({
  filter: override,
  children,
}: PropsWithChildren<{
  filter: (filter: PageFilter | undefined) => PageFilter | undefined;
}>) => {
  const context = useContext(FilterStackContext);

  if (!context) {
    throw new Error('FilterOverrideProvider must be used within a FilterSourceProvider');
  }

  return (
    <FilterStackContext.Provider
      value={{
        ...context,
        filter: useMemo(() => override(context.filter), [override, context.filter]),
      }}
    >
      {children}
    </FilterStackContext.Provider>
  );
};

export const useFilterStackContext = () => {
  const context = useContext(FilterStackContext);

  if (!context) {
    throw new Error('useFilterStack must be used within a FilterSourceProvider/FilterOverrideProvider');
  }

  return context;
};

/**
 * You shouldn't call useSourceFilter in places where you define **FilterSourceProvider** or **FilterOverrideProvider** because it will cause **premature** resolve of the filter
 */
export const useSourceFilter = () => {
  const {source, resolve} = useFilterStackContext();

  useEffect(() => {
    resolve();
  }, [resolve]);

  return source;
};

/**
  You shouldn't call usePageFilter in places where you define **FilterSourceProvider** or **FilterOverrideProvider** because it will cause **premature** resolve of the filter
 */
export const usePageFilter = () => {
  const {filter, resolve} = useFilterStackContext();

  useEffect(() => {
    resolve();
  }, [resolve]);

  return filter;
};

/**
 You shouldn't call useFilterLoading in places where you define **FilterSourceProvider** or **FilterOverrideProvider** because it will cause **premature** resolve of the filter
 */
export const useFilterLoading = () => {
  const {loading, resolve} = useFilterStackContext();

  useEffect(() => {
    resolve();
  }, [resolve]);

  return loading;
};
